// tierService.js - User tier and feature access service
// DriftAlarm.AI - Built by Security Experts

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://driftalarm-functions-bbfmfuh5hmdsfcc7.eastus-01.azurewebsites.net/api';

/**
 * Helper to get auth token
 */
async function getAuthToken() {
    try {
        const msalInstance = window.msalInstance;
        if (!msalInstance) return null;

        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) return null;

        const request = {
            scopes: ['openid', 'profile', 'email'],
            account: accounts[0],
        };

        const response = await msalInstance.acquireTokenSilent(request);
        return response.idToken;
    } catch (error) {
        console.log('Failed to acquire token:', error.message);
        return null;
    }
}

/**
 * Get headers with auth token
 */
async function getHeaders() {
    const token = await getAuthToken();
    const headers = {
        'Content-Type': 'application/json',
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
}

/**
 * Get user tier information
 * @returns {Promise<object>} Tier info including features and usage
 */
export async function getUserTier() {
    // PRD-81 A3: try twice before giving up. A single transient blip on
    // /user-tier used to strand the user on a fake trial object (and, worse,
    // skip the server-side ensureUserProfile bootstrap). One retry sharply cuts
    // the odds of that, and getCachedTierInfo refuses to cache the fallback.
    const MAX_ATTEMPTS = 2;
    let lastError = null;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
        try {
            const headers = await getHeaders();

            const response = await fetch(`${API_BASE_URL}/user-tier`, {
                method: 'GET',
                headers,
            });

            if (!response.ok) {
                throw new Error(`Failed to get tier info (${response.status})`);
            }

            return await response.json();
        } catch (error) {
            lastError = error;
            console.error(`Error getting user tier (attempt ${attempt}/${MAX_ATTEMPTS}):`, error.message);
        }
    }

    // All attempts failed. Return a trial-shaped fallback so the UI can still
    // render, but flag it with _fallback so getCachedTierInfo does NOT persist
    // it into the 60s cache — the next call re-fetches promptly. Consumers read
    // .tier / .features and ignore _fallback.
    console.error('getUserTier: returning uncached trial fallback after retries', lastError?.message);
    return {
        _fallback: true,
        authenticated: false,
        tier: 'trial',
        tierName: 'Trial',
        isTrialExpired: false,
        daysRemaining: 0,
        features: {
            fastScansPerWeek: 10,
            includeVulnCheck: true,
            includeValidation: false,
            deepScansPerWeek: 5,
            scrutinizePerWeek: 5,
            maxSchedules: 3,
            maxTotalAssets: 2
        },
        usage: {
            fastScansThisWeek: 0,
            deepScansThisWeek: 0,
            scheduledScansThisWeek: 0
        }
    };
}

/**
 * Check if user can use a specific feature
 * @param {string} feature - Feature name
 * @returns {Promise<object>} { allowed, reason, upgradeRequired }
 */
export async function canUseFeature(feature) {
    try {
        const tierInfo = await getUserTier();
        const { features, usage } = tierInfo;

        switch (feature) {
            case 'vulnCheck':
                return {
                    allowed: features.includeVulnCheck,
                    reason: features.includeVulnCheck ? 'OK' : 'Vulnerability scanning requires Standard tier or higher',
                    upgradeRequired: !features.includeVulnCheck ? 'standard' : null
                };

            case 'validate':
                return {
                    allowed: features.includeValidation || features.validateEnabled,
                    reason: 'OK',
                    upgradeRequired: null
                };

            case 'fastScan': {
                const fastLimit = features.fastScansPerWeek;
                const fastUsed = usage?.fastScansThisWeek || 0;
                if (fastLimit === -1 || fastLimit === null) {
                    return { allowed: true, reason: 'Unlimited' };
                }
                return {
                    allowed: fastUsed < fastLimit,
                    reason: fastUsed >= fastLimit
                        ? `Weekly limit reached (${fastUsed}/${fastLimit})`
                        : `${fastLimit - fastUsed} scans remaining`,
                    limit: fastLimit,
                    used: fastUsed
                };
            }

            case 'scrutinize':
            case 'deepScan': {
                const deepLimit = features.deepScansPerWeek || features.scrutinizePerWeek;
                const deepUsed = usage?.deepScansThisWeek || usage?.scrutinizeThisWeek || 0;
                if (deepLimit === null) {
                    return { allowed: true, reason: 'Unlimited' };
                }
                if (deepLimit === 0) {
                    return {
                        allowed: false,
                        reason: 'Deep scans require Standard tier or higher',
                        upgradeRequired: 'standard'
                    };
                }
                return {
                    allowed: deepUsed < deepLimit,
                    reason: deepUsed >= deepLimit
                        ? `Weekly limit reached (${deepUsed}/${deepLimit})`
                        : `${deepLimit - deepUsed} scans remaining`,
                    limit: deepLimit,
                    used: deepUsed
                };
            }

            case 'assets':
                const assetLimit = features.maxTotalAssets;
                if (assetLimit === 0) {
                    return {
                        allowed: false,
                        reason: 'Asset monitoring requires Standard tier or higher',
                        upgradeRequired: 'standard'
                    };
                }
                return { allowed: true, limit: assetLimit };

            default:
                return { allowed: false, reason: 'Unknown feature' };
        }
    } catch (error) {
        console.error('Error checking feature access:', error);
        return { allowed: false, reason: 'Unable to verify access' };
    }
}

/**
 * Get all available tiers for pricing display
 */
export async function getAllTiers() {
    try {
        const tierInfo = await getUserTier();
        return tierInfo.allTiers || [];
    } catch (error) {
        console.error('Error getting tiers:', error);
        return [];
    }
}

/**
 * Cache for tier info to reduce API calls
 */
let tierCache = null;
let tierCacheTime = 0;
const CACHE_DURATION = 60000; // 1 minute

export async function getCachedTierInfo() {
    const now = Date.now();
    if (tierCache && (now - tierCacheTime) < CACHE_DURATION) {
        return tierCache;
    }

    const result = await getUserTier();
    // PRD-81 A3: never cache the error fallback — caching it would freeze the
    // user on a fake trial object for the full 60s TTL. Only persist a real
    // (fetched) response so the next call re-fetches a failed lookup promptly.
    if (!result._fallback) {
        tierCache = result;
        tierCacheTime = now;
    }
    return result;
}

/**
 * Clear tier cache (call after upgrade or scan completion)
 */
export function clearTierCache() {
    tierCache = null;
    tierCacheTime = 0;
}

// Default export
const tierService = {
    getUserTier,
    canUseFeature,
    getAllTiers,
    getCachedTierInfo,
    clearTierCache
};

export default tierService;
